/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';

import TopicGroup from './TopicTable/TopicGroup';
import TopicSum from './TopicTable/TopicSum';
import FormField from '../Application/FormField/FormField';
import RoundButton from '../RoundButton/RoundButton';
import shortid from 'shortid';

import styles from './TopicStructure.module.scss';

// Defined the number of years in a group, when grouping years
const noOfYearsInGroup = 2;

/**
 * This is the TopicStructure component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function TopicStructure({ settings, content, labels }) {
  const [id] = useState(shortid.generate());
  const useLargeLayout = useMediaQuery({
    query: `(min-width: ${styles['page-width-tablet-narrow']})`,
  });

  const subjects = content.map((c) => c.subject);
  const [subject, setSubject] = useState(subjects[0]);
  const [collapsed, setCollapsed] = useState([]);

  const groups = content.find((c) => c.subject === subject)?.groups;

  // Determine how many years the topics is spread across
  const maxYears = groups?.reduce((curr, g) => {
    const max = g.topics.reduce((c, t) => {
      // Get max number of years a topic is spread across
      const max2 = Object.keys(t.points).reduce((c2, k) => {
        return t.points[k] !== null && k > c2 ? parseInt(k, 10) : c2;
      }, 0);
      return max2 > c ? max2 : c;
    }, 0);
    return max > curr ? max : curr;
  }, 0);

  const [yearFilter, setYearFilter] = useState(
    useLargeLayout ? [1, maxYears] : [1, Math.min(maxYears, noOfYearsInGroup)]
  );

  const yearGroups = Math.ceil(maxYears / noOfYearsInGroup);

  const handleToggleGroup = (group) => {
    const nextCollapsed = collapsed.includes(group)
      ? collapsed.filter((g) => g !== group)
      : [...collapsed, group];
    setCollapsed(nextCollapsed);
  };

  // console.log({ groups });

  // Sum points per course, per year, per group
  const accumulatedYearlyPoints = groups.reduce((curr, g) => {
    // Add points of all topics in group
    const groupPoints = g.topics.reduce((curr, t) => {
      const next = Object.keys(t.points).reduce((c, i) => {
        const n = { ...c };
        const p = t.points[i];

        n[i] = !n[i] ? p : n[i] + p;

        return n;
      }, curr);

      return next;
    }, {});

    // Merge yearly points in group, with previous groups and reduce to new object
    const next = Object.keys(groupPoints).reduce((c, y) => {
      c[y] = groupPoints[y] + (c[y] ?? 0);
      return c;
    }, curr);

    return next;
  }, {});

  // console.log({ accumulatedYearlyPoints });

  // Toggle year filter when switcing between large and !large layouts
  useEffect(() => {
    if (!useLargeLayout) {
      setYearFilter([1, Math.min(maxYears, noOfYearsInGroup)]);
    } else {
      setYearFilter([1, maxYears]);
    }
  }, [useLargeLayout, maxYears]);

  return (
    <div className={classNames(styles.main, {})}>
      {subjects.length > 1 && (
        <FormField
          className={styles.dropdown}
          type="select"
          label={labels.formLabel}
          value={subject}
          options={subjects.map((s) => ({ label: s, value: s }))}
          onChange={(value) => setSubject(value)}
          required
          standalone
          aria-controls={id}
        />
      )}
      {!useLargeLayout && yearGroups > 1 && (
        <div className={styles.filter}>
          {Array.from(Array(yearGroups).keys()).map((y) => {
            const filterGroup = [
              y * noOfYearsInGroup + 1,
              y * noOfYearsInGroup + noOfYearsInGroup,
            ];

            const selected =
              yearFilter[0] === filterGroup[0] &&
              yearFilter[1] === filterGroup[1];

            return (
              <RoundButton
                key={y}
                variant={selected ? 'default' : 'outline'}
                onClick={() => {
                  setYearFilter(filterGroup);
                }}
                role="tab"
                aria-selected={selected ? 'true' : 'false'}
                aria-controls={id}
              >
                {filterGroup[0]}.{' '}
                {filterGroup[1] > filterGroup[0] + 1 ? '-' : '&'}{' '}
                {filterGroup[1]}. år
              </RoundButton>
            );
          })}
        </div>
      )}
      {groups && (
        <div id={id} aria-live="polite">
          {groups.map((g) => (
            <TopicGroup
              key={`${subject}-${g.group}`}
              title={g.group}
              topics={g.topics}
              filterYears={yearFilter}
              view={useLargeLayout ? 'desktop' : 'mobile'}
              onToggleGroup={handleToggleGroup}
              collapsed={collapsed.includes(g.group)}
              labels={labels}
              summarize={settings.summarize}
            />
          ))}
          {settings.summarize && (
            <TopicSum
              title={labels.pointsPerYear}
              filterYears={yearFilter}
              view={useLargeLayout ? 'desktop' : 'mobile'}
              labels={labels}
              points={accumulatedYearlyPoints}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
TopicStructure.propTypes = {
  settings: PropTypes.shape({
    summarize: PropTypes.bool,
  }),
  content: PropTypes.arrayOf(
    PropTypes.shape({
      subject: PropTypes.string,
      groups: PropTypes.arrayOf(
        PropTypes.shape({
          group: PropTypes.string,
          topics: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string,
              code: PropTypes.string,
              url: PropTypes.string,
              points: PropTypes.shape({
                1: PropTypes.number,
                2: PropTypes.number,
                3: PropTypes.number,
                4: PropTypes.number,
              }),
            })
          ),
        })
      ),
    })
  ),
  labels: PropTypes.shape({
    formLabel: PropTypes.string,
    btnExpand: PropTypes.string,
    btnPullTogether: PropTypes.string,
    code: PropTypes.string,
    topic: PropTypes.string,
    pointsPerYear: PropTypes.string,
    pointsPerYearShort: PropTypes.string,
    total: PropTypes.string,
    year: PropTypes.string,
    empty: PropTypes.string,
    sum: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
TopicStructure.defaultProps = {
  settings: {
    summarize: true,
  },
  content: [],
  labels: {
    credits: 'credits',
    formLabel: 'formLabel',
    btnExpand: 'btnExpand',
    btnPullTogether: 'btnPullTogether',
    code: 'code',
    topic: 'topic',
    pointsPerYear: 'pointsPerYear',
    pointsPerYearShort: 'pointsPerYearShort',
    total: 'total',
    year: 'year',
    empty: 'empty',
    sum: 'sum',
  },
};

export default TopicStructure;
